import { Combobox, Label, Field } from "@headlessui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { SwitchButton } from "@/components/search";
import { Spinner } from "@/components/common/svgs";
import { getLocationString } from "@/utils/text";
import { API } from "@/services/API";
import { hasDigits } from "@/utils/query";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { useDispatch } from "react-redux";
import { useIsAnalyticRoute } from "@/routing";
import { getMarketFromQuery } from "@/utils/zips";
import { Check, X } from "lucide-react";
import { cn } from "@/utils/css";

export const LocationDropdown = ({
    name,
    urlQueryValues = {},
    value,
    setValue,
    query,
    setQuery,
    label,
    className,
    width,
    handleSwitch,
    disabled = false,
    theme,
    placeholder = "City or ZIP",
    isVisible = true,
}) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const errorText = "Error getting location list";
    const { zip, placeName, stateCode, withZip } = urlQueryValues;
    const [showZip, setShowZip] = useState(!!withZip);
    const dispatch = useDispatch();
    const isAnalyticRoute = useIsAnalyticRoute();
    const [isTyping, setIsTyping] = useState(Math.random());
    const isTruck = theme === "truck";
    const abortControllerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isAnalyticRoute) {
            clearStates(true);
        }
    }, [isAnalyticRoute]);

    const debouncedFetchData = useCallback(
        debounce((value) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            const { signal } = abortControllerRef.current;

            setLoading(true);
            if (value.length > 2) {
                API.get("/search/suggestions", {
                    params: {
                        name: value,
                    },
                    signal,
                })
                    .then((response) => {
                        setResults(response);
                    })
                    .catch((error) => {
                        if (signal.aborted) return;
                        setResults([]);
                        setError(errorText);
                        console.log(errorText, error);
                    })
                    .finally(() => {
                        if (!signal.aborted) setLoading(false);
                    });
            } else if (value.length === 0) {
                API.get("/analytics/search-history", {
                    params: {
                        "number-of-responses": "10",
                    },
                    signal,
                })
                    .then((response) => {
                        const placeKV = {};
                        const places = [];
                        response.searches?.forEach((search) => {
                            placeKV[`${search.pickUp.placeName}-${search.pickUp.stateCode}`] = search.pickUp;
                            if (search.dropOff) {
                                placeKV[`${search.dropOff.placeName}-${search.dropOff.stateCode}`] = search.dropOff;
                            }
                        });
                        Object.keys(placeKV).forEach((key) => places.push(placeKV[key]));
                        setResults(places);
                    })
                    .catch((error) => {
                        if (signal.aborted) return;
                        setError(errorText);
                        console.log(errorText, "history", error);
                        setResults([]);
                    })
                    .finally(() => {
                        if (!signal.aborted) setLoading(false);
                    });
            } else {
                setResults([]);
                setLoading(false);
            }
        }, 500),
        [],
    );

    const clearStates = () => {
        setQuery("");
        setValue(null);
        setResults([]);
        setLoading(false);
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.blur();
        }
    };

    useEffect(() => {
        return () => {
            clearStates();
        };
    }, []);

    useEffect(() => {
        if (disabled) return;
        setShowZip(hasDigits(query));
        debouncedFetchData(query || "");
    }, [isTyping, disabled]);

    // When user types, we set different query value
    const handleInputChange = (event) => {
        debouncedFetchData.cancel();
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setResults([]);
        setQuery(event.target.value);
        setIsTyping(Math.random());
    };

    // When width is 0 reset values
    useEffect(() => {
        // Skip this effect for truck theme - it manages its own state externally
        if (isTruck) return;

        if (!width) {
            clearStates();
        }
    }, [width]);

    useEffect(() => {
        // Skip this effect for truck theme - it manages its own state externally
        if (isTruck) return;

        const isPickup = name === "pickup";
        let valuesToSet = isPickup ? { pickup: null, pickupQuery: "" } : { delivery: null, deliveryQuery: "" };
        let newQuery = "";
        let newValue = null;
        if (zip && placeName && stateCode) {
            newValue = getMarketFromQuery(zip, placeName, stateCode);
            newQuery = getLocationString(newValue, withZip);
            valuesToSet = isPickup
                ? {
                      pickupQuery: newQuery,
                      pickup: newValue,
                  }
                : {
                      deliveryQuery: newQuery,
                      delivery: newValue,
                  };
        }
        setQuery(newQuery);
        dispatch(setCurrentSearch(valuesToSet));
        setValue(newValue);
    }, [zip]);

    useEffect(() => {
        // Skip this effect for truck theme - it manages its own state externally
        if (isTruck) return;

        let newQuery = "";
        let newValue = null;
        if (width && zip && placeName && stateCode) {
            newValue = getMarketFromQuery(zip, placeName, stateCode);
            newQuery = getLocationString(newValue, withZip);
        }
        setQuery(newQuery);
        setValue(newValue);
    }, [width]);

    return (
        <div
            className={cn(
                "flex flex-row bg-cm-p-surface-container items-center z-20 relative min-w-0",
                {
                    "rounded-[6px] border border-cm-p-outline bg-cm-p-surface-container focus-within:border-cm-p-accent-200": isTruck,
                    "opacity-40": disabled,
                },
                className,
            )}
        >
            <Field
                className={`flex flex-col ${isTruck ? "h-[40px]" : "h-[64px] px-4 py-[10px]"} w-full md:min-w-[${width}px] md:max-w-[${width}px] relative transition-all ease-linear duration-50`}
            >
                <Label className="text-sm text-cm-p-text-secondary">{label}</Label>
                <Combobox
                    value={value}
                    onChange={(newValue) => {
                        setValue(newValue);
                        if (newValue) {
                            setQuery(getLocationString(newValue, showZip));
                            setResults([]);
                        }
                        setResults([]);
                    }}
                    immediate
                >
                    <div className={isTruck ? "flex items-center w-full" : "w-full"}>
                        <Combobox.Input
                            ref={inputRef}
                            className={cn(
                                "bg-cm-p-surface-container border-none !important outline-none focus:border-transparent focus:border-none focus:outline-none focus:ring-0 p-0 text-cm-p-text-primary placeholder:text-cm-p-text-secondary font-medium placeholder-normal",
                                {
                                    "cursor-not-allowed": disabled,
                                    "w-full": !isTruck,
                                    "flex-1 min-w-0 px-3 py-2 rounded-[6px] text-[15px] placeholder:text-[15px] placeholder:font-normal placeholder:text-cm-p-text-secondary bg-transparent":
                                        isTruck,
                                },
                            )}
                            placeholder={placeholder}
                            onChange={handleInputChange}
                            displayValue={(location) => (location ? getLocationString(location, showZip) : query || "")}
                            autoComplete="off"
                            data-open
                            disabled={disabled}
                            onBlur={() => {
                                if (value && query !== getLocationString(value, showZip)) {
                                    setValue(null);
                                }
                            }}
                            onFocus={handleInputChange}
                        />
                        {isTruck && isVisible && (value || query) && (
                            <button
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    clearStates();
                                }}
                                className="shrink-0 px-2 text-cm-p-text-secondary hover:text-cm-p-text-primary"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    {loading || (results.length === 0 && query && query.length > 0) || results.length ? (
                        <Combobox.Options
                            className={`${isTruck ? "absolute z-40 mt-[5px] top-full left-0 w-full" : "absolute z-30 mt-[5px] top-[64px] left-0 w-full"} max-h-[289px] overflow-auto rounded-lg px-[1px] border border-cm-p-outline drop-shadow-[0_4px_4px_rgba(0,0,0,0.08)] bg-cm-p-surface ${query !== "" || loading ? "py-[1px]" : ""}`}
                        >
                            {loading || (results.length === 0 && query && query.length > 0) ? (
                                <div className="relative cursor-default select-none py-2 px-3 text-cm-p-text-secondary min-h-[41px] flex items-center">
                                    {query.length > 0 && query.length < 3 ? (
                                        "Min 3 characters."
                                    ) : loading ? (
                                        <span className="inline-flex flex-row items-center">
                                            Loading <Spinner className="w-3 h-3 ml-4" />
                                        </span>
                                    ) : (
                                        "No results found."
                                    )}
                                </div>
                            ) : results.length ? (
                                results.map((result, idx) => (
                                    <Combobox.Option
                                        key={idx}
                                        value={result}
                                        className="relative w-full cursor-pointer select-none inline-flex min-h-[41px] px-3 items-center justify-between rounded-lg hover:bg-cm-p-surface-hover data-[active]:bg-cm-p-surface-hover"
                                    >
                                        <>
                                            <span className="block truncate text-cm-p-text-primary font-medium text-sm">
                                                {`${getLocationString(result, showZip)}`}
                                            </span>
                                            {value?.zip === result.zip ? <Check className="size-4 text-cm-p-text-secondary" /> : null}
                                        </>
                                    </Combobox.Option>
                                ))
                            ) : null}
                        </Combobox.Options>
                    ) : null}
                </Combobox>
            </Field>
            {handleSwitch && <SwitchButton onClick={handleSwitch} />}
        </div>
    );
};
